import { motion } from "framer-motion";
import { Download as DownloadIcon, ArrowLeft, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { useLenis } from "../hooks/useLenis";
import { Nav } from "../components/site/Nav";
import { Footer } from "../components/site/Footer";
import { DOWNLOAD_PLATFORMS, DOWNLOAD_URL, ABSTRACT_ORB } from "../lib/data";
import { useLanguage } from "../lib/i18n";

const EASE = [0.16, 1, 0.3, 1];

export default function Download() {
  useLenis();
  const { t } = useLanguage();

  return (
    <div className="relative noise-overlay bg-[#040814] min-h-screen">
      <Nav />
      <main className="relative pt-40 pb-28 overflow-hidden grid-lines" data-testid="download-page">
        <img src={ABSTRACT_ORB} alt="" className="pointer-events-none absolute -top-32 -right-40 w-[60vw] max-w-[800px] opacity-40 mix-blend-screen select-none" />
        <div className="pointer-events-none absolute top-1/4 left-0 w-[500px] h-[500px] bg-blue-600/15 rounded-full blur-[150px]" />

        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }} className="max-w-3xl">
            <span className="text-xs uppercase tracking-[0.2em] text-blue-400 font-bold">{t("download.label")}</span>
            <h1 className="font-head font-semibold text-4xl md:text-6xl tracking-tighter leading-[1.05] mt-4 text-gradient">
              {t("download.title")}
            </h1>
            <p className="mt-6 text-lg text-slate-400 leading-relaxed max-w-xl">{t("download.subtitle")}</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mt-16">
            {DOWNLOAD_PLATFORMS.map((plat, i) => {
              const Icon = plat.icon;
              return (
                <motion.div
                  key={plat.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: EASE, delay: 0.15 + i * 0.1 }}
                  className="group relative rounded-3xl border border-white/[0.06] bg-[#0A101F] p-8 flex flex-col overflow-hidden hover:border-white/[0.15] hover:-translate-y-1.5 transition-[transform,border-color] duration-500"
                  data-testid={`download-card-${plat.id}`}
                >
                  <div className="absolute -top-24 -right-24 w-56 h-56 bg-blue-500/10 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                  <div className="relative w-14 h-14 rounded-2xl bg-blue-500/10 border border-blue-400/20 flex items-center justify-center text-blue-400 mb-6 group-hover:scale-110 transition-transform duration-500">
                    <Icon size={26} strokeWidth={1.8} />
                  </div>
                  <h3 className="font-head text-2xl tracking-tight mb-1.5">{t(`download.platforms.${plat.id}.name`)}</h3>
                  <p className="text-slate-400 text-[15px] leading-relaxed mb-8 flex-1">{t(`download.platforms.${plat.id}.desc`)}</p>

                  <a
                    href={DOWNLOAD_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 w-full py-3.5 rounded-full bg-blue-500 text-white font-semibold hover:bg-blue-600 transition-colors duration-300"
                    data-testid={`download-btn-${plat.id}`}
                  >
                    <DownloadIcon size={17} /> {t("download.directDownload")}
                  </a>

                  <div className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-500">
                    <Clock size={13} />
                    <span>{t("download.badgeSoon")}</span>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-14 text-center text-sm text-slate-500 max-w-2xl mx-auto"
            data-testid="download-store-note"
          >
            {t("download.comingSoonStores")}
          </motion.p>

          <div className="mt-12 flex justify-center">
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/10 text-slate-300 hover:text-white hover:border-white/25 transition-colors duration-300" data-testid="download-back-btn">
              <ArrowLeft size={16} /> {t("download.back")}
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
